/**
 * Extracts a clear, human-readable error message from any error object.
 * Works with Axios errors, Firebase errors, Salesforce errors, and plain JS errors.
 */
export function getErrorMessage(error) {
  if (!error) return "Unknown error occurred.";

  // --- Axios / HTTP errors ---
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    // Salesforce-style errors: [{ message, errorCode }]
    if (Array.isArray(data) && data.length > 0) {
      const sfErrors = data.map((e) => `[${e.errorCode || "ERROR"}] ${e.message}`).join(" | ");
      return `Salesforce error (${status}): ${sfErrors}`;
    }

    // Salesforce OAuth error: { error, error_description }
    if (data?.error_description) {
      return `Auth error (${status}): ${data.error_description}`;
    }

    // Generic server error with message field
    if (data?.message) {
      return `Server error (${status}): ${data.message}`;
    }

    if (data?.error) {
      return `Server error (${status}): ${data.error}`;
    }

    // Status code descriptions
    const statusMessages = {
      400: "Bad request — the server could not understand the request.",
      401: "Authentication failed — access token expired or invalid.",
      403: "Access denied — you don't have permission for this resource.",
      404: "Not found — the record or endpoint does not exist.",
      500: "Internal server error — something went wrong on the server.",
      502: "Bad gateway — the server received an invalid response.",
      503: "Service unavailable — the server is temporarily down.",
    };

    return statusMessages[status] || `HTTP error ${status}: ${JSON.stringify(data) || "No details available."}`;
  }

  // --- Network errors (no response received) ---
  if (error.request) {
    if (error.code === "ECONNABORTED") {
      return "Request timed out — the server took too long to respond. Please check your internet connection.";
    }
    return "Network error — no response from server. Please check your internet connection or try again later.";
  }

  // --- Firebase Auth errors ---
  if (error.code) {
    const firebaseMessages = {
      "auth/invalid-credential": "Invalid email or password. Please double-check your credentials.",
      "auth/user-not-found": "No account found with this email address.",
      "auth/wrong-password": "Incorrect password. Please try again.",
      "auth/email-already-in-use": "This email is already registered. Try logging in instead.",
      "auth/weak-password": "Password is too weak. Use at least 6 characters.",
      "auth/missing-email": "Please enter your email address.",
      "auth/too-many-requests": "Too many failed attempts. Please wait a few minutes and try again.",
      "auth/network-request-failed": "Network error. Please check your internet connection.",
      "auth/popup-closed-by-user": "Sign-in popup was closed before completing.",
      "auth/cancelled-popup-request": "Sign-in was cancelled.",
    };
    return firebaseMessages[error.code] || `Error (${error.code}): ${error.message || "Unknown error."}`;
  }

  // --- Plain JS Error ---
  if (error.message) {
    return error.message;
  }

  // --- Fallback ---
  return String(error);
}

/**
 * Returns a short error summary suitable for UI display,
 * plus the full detail for a collapsible/expandable section.
 */
export function getErrorDetail(error) {
  const message = getErrorMessage(error);
  const status = error?.response?.status || null;
  const url = error?.config?.url || null;

  return { message, status, url };
}
